import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export interface DataTableColumnSelectorOption {
  disabled?: boolean
  id: string
  label: string
}

export interface DataTableColumnSelectorProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  onValueChange: (value: string[]) => void
  options: DataTableColumnSelectorOption[]
  value: string[]
}

export function DataTableColumnSelector({
  children,
  className,
  disabled,
  onValueChange,
  options,
  value,
  ...props
}: DataTableColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const selectedValues = new Set(value)
  const selectedCount = options.filter((option) => selectedValues.has(option.id)).length

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  function toggleOption(optionId: string) {
    const nextValues = new Set(selectedValues)

    if (nextValues.has(optionId)) {
      if (selectedCount <= 1) {
        return
      }
      nextValues.delete(optionId)
    } else {
      nextValues.add(optionId)
    }

    onValueChange(options.filter((option) => nextValues.has(option.id)).map((option) => option.id))
  }

  return (
    <div className={cn('relative h-8 text-sm', className)} ref={rootRef}>
      <button
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'inline-flex h-full min-w-[72px] cursor-pointer items-center justify-center gap-1.5 rounded-md border border-[var(--color-control-border)] bg-white px-3 font-medium text-[var(--color-text)] shadow-[var(--shadow-control)] transition-colors outline-none',
          'hover:border-[var(--color-control-border-hover)] hover:bg-[var(--color-surface-muted)] focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)]',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
        disabled={disabled}
        {...props}
        onClick={(event) => {
          props.onClick?.(event)
          setIsOpen((current) => !current)
        }}
        onKeyDown={(event) => {
          props.onKeyDown?.(event)
          if (event.key === 'Escape') {
            setIsOpen(false)
            return
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setIsOpen(true)
          }
        }}
        ref={buttonRef}
        type="button"
      >
        <ColumnsIcon />
        <span>{children}</span>
        <ChevronIcon isOpen={isOpen} />
      </button>
      {/* 字段多的表格（如对话明细的 32 列）菜单会超出视口，父级 overflow-hidden 下够不到底部选项，因此限高并滚动。 */}
      {isOpen ? (
        <div
          className="absolute top-full right-0 z-50 mt-2 max-h-[min(420px,60vh)] w-[220px] overflow-y-auto rounded-lg border border-[var(--color-panel-border)] bg-white p-2 shadow-[0_14px_34px_rgba(26,28,25,0.14)]"
          id={menuId}
          role="menu"
        >
          {options.map((option) => {
            const checked = selectedValues.has(option.id)
            const optionDisabled = (option.disabled ?? false) || (checked && selectedCount <= 1)

            return (
              <label
                className={cn(
                  'flex h-9 cursor-pointer items-center gap-3 rounded-md px-2.5 text-sm whitespace-nowrap text-[var(--color-text)] transition-colors hover:bg-[var(--color-hover-muted)]',
                  optionDisabled ? 'cursor-not-allowed opacity-55' : null,
                )}
                key={option.id}
              >
                <input
                  checked={checked}
                  className="sr-only"
                  disabled={optionDisabled}
                  onChange={() => toggleOption(option.id)}
                  type="checkbox"
                />
                <span
                  aria-hidden
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors',
                    checked
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                      : 'border-[var(--color-control-border)] bg-white text-transparent',
                  )}
                >
                  <CheckIcon />
                </span>
                <span className="min-w-0 truncate">{option.label}</span>
              </label>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function CheckIcon() {
  return (
    <svg className="size-3" fill="none" viewBox="0 0 12 12">
      <path
        d="m3 6.2 1.8 1.8L9 3.8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      aria-hidden
      className={cn(
        'size-3 text-[var(--color-text-muted)] transition-transform',
        isOpen ? 'rotate-180' : null,
      )}
      fill="none"
      viewBox="0 0 12 12"
    >
      <path
        d="m3 4.5 3 3 3-3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function ColumnsIcon() {
  return (
    <svg
      aria-hidden
      className="size-4 text-[var(--color-text-muted)]"
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="M3.5 3.5h9M3.5 8h9M3.5 12.5h9"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.7"
      />
    </svg>
  )
}
